import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import { LocalizedLink } from "@/i18n/language";
import { img } from "@/lib/images";
import KuebelGallery from "@/pages/vw181-kuebelwagen/components/KuebelGallery";

const HEADER_IMAGE = img("balivwtour/vw181-kuebelwagen/vw181cars06.jpg");

const articleSections: { titleKey: string; paragraphKeys: string[] }[] = [
  { titleKey: "kw_intro_title", paragraphKeys: ["kw_intro_p1"] },
  { titleKey: "kw_hist_title", paragraphKeys: ["kw_hist_p1", "kw_hist_p2"] },
  { titleKey: "kw_bali_title", paragraphKeys: ["kw_bali_p1", "kw_bali_p2"] },
  { titleKey: "kw_tech_title", paragraphKeys: ["kw_tech_p1", "kw_tech_p2"] },
];

const facts: { labelKey: string; valueKey: string }[] = [
  { labelKey: "kw_fact_year_label", valueKey: "kw_fact_year_value" },
  { labelKey: "kw_fact_engine_label", valueKey: "kw_fact_engine_value" },
  { labelKey: "kw_fact_power_label", valueKey: "kw_fact_power_value" },
  { labelKey: "kw_fact_weight_label", valueKey: "kw_fact_weight_value" },
  { labelKey: "kw_fact_seats_label", valueKey: "kw_fact_seats_value" },
];

const sidebarLinks = [
  { to: "/tour-info", labelKey: "kw_sidebar_tourinfo" },
  { to: "/day-trips", labelKey: "kw_sidebar_daytrips" },
  { to: "/multi-day-tours", labelKey: "kw_sidebar_multiday" },
  { to: "/villas", labelKey: "kw_sidebar_villas" },
];

export default function KuebelwagenPage() {
  const { t } = useTranslation();

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Navbar />
      <main>
        <PageHeader
          eyebrow={t("kw_eyebrow")}
          title={t("kw_title")}
          subtitle={t("kw_subtitle")}
          image={HEADER_IMAGE}
          imageAlt="Bali VW Tour – VW 181 Kübelwagen auf einer Landstraße zwischen Reisfeldern"
        />

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
              <article className="lg:col-span-2 space-y-9">
                {articleSections.map((section) => (
                  <div key={section.titleKey}>
                    <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-3">
                      {t(section.titleKey)}
                    </h2>
                    <div className="w-10 h-0.5 bg-accent-500 mb-4"></div>
                    <div className="space-y-4">
                      {section.paragraphKeys.map((key) => (
                        <p key={key} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                          {t(key)}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </article>

              <aside className="lg:col-span-1">
                <div className="sticky top-24 space-y-6">
                  <div className="bg-background-100 border border-background-200 rounded-lg p-6">
                    <div className="eyebrow-brand text-xs uppercase tracking-[0.3em] mb-4">
                      — {t("kw_facts_title")} —
                    </div>
                    <dl className="space-y-3">
                      {facts.map((fact) => (
                        <div
                          key={fact.labelKey}
                          className="flex items-start justify-between gap-4 border-b border-background-200 pb-3 last:border-0 last:pb-0"
                        >
                          <dt className="text-xs uppercase tracking-[0.12em] text-foreground-600">
                            {t(fact.labelKey)}
                          </dt>
                          <dd className="text-sm font-medium text-foreground-950 text-right">
                            {t(fact.valueKey)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>

                  <div className="bg-background-100 border border-background-200 rounded-lg p-6">
                    <div className="eyebrow-brand text-xs uppercase tracking-[0.3em] mb-3">
                      — {t("kw_sidebar_title")} —
                    </div>
                    <nav className="space-y-1">
                      {sidebarLinks.map((link) => (
                        <LocalizedLink
                          key={link.to}
                          to={link.to}
                          className="block px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-700 transition-colors cursor-pointer"
                        >
                          {t(link.labelKey)}
                        </LocalizedLink>
                      ))}
                      <span className="block px-3 py-2.5 rounded-md text-sm font-medium bg-accent-50 text-accent-700">
                        {t("kw_title")}
                      </span>
                    </nav>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <KuebelGallery />

        <section className="py-14 md:py-20 bg-primary-700">
          <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <h2 className="font-heading text-2xl md:text-4xl text-background-50 leading-tight mb-3">
                {t("kw_cta_title")}
              </h2>
              <p className="text-background-200 text-sm md:text-base leading-relaxed">
                {t("kw_cta_text")}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              <LocalizedLink
                to="/day-trips"
                className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 btn-on-dark text-background-50 px-7 py-3.5 rounded-md font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                {t("kw_cta_tours")}
                <i className="ri-arrow-right-line"></i>
              </LocalizedLink>
              <LocalizedLink
                to="/contact"
                className="inline-flex items-center justify-center gap-2 border border-background-50/50 text-background-50 hover:bg-accent-100 hover:border-accent-400 hover:text-foreground-950 px-7 py-3.5 rounded-md font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className="ri-mail-line"></i>
                {t("kw_cta_contact")}
              </LocalizedLink>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}